import React from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Layout } from '@/components/layout/Layout'
import { Dashboard } from '@/pages/Dashboard'
import { LessonSession } from '@/pages/LessonSession'

import { Settings } from '@/pages/Settings'
import { Home } from '@/pages/Home'
import { PotatoPiratesAuth } from '@/pages/PotatoPiratesAuth'
import { Lessons } from '@/pages/Lessons'
import { Courses } from '@/pages/Courses'
import { CourseDetail } from '@/pages/CourseDetail'
import { Organizations } from '@/pages/Organizations'
import { Plans } from '@/pages/Plans'
import { Checkout } from '@/pages/Checkout'
import { AddPaymentMethod } from '@/pages/AddPaymentMethod'
import { CheckoutSuccess } from '@/pages/CheckoutSuccess'
import { LessonBuilder } from '@/pages/LessonBuilder'
import { GuestJoin } from '@/pages/GuestJoin'
import { GuestPlay } from '@/pages/GuestPlay'
import { OAuthCallback } from '@/pages/OAuthCallback'
import { VerifyEmail } from '@/pages/VerifyEmail'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { LessonReport } from '@/pages/LessonReport'
import { CourseReport } from '@/pages/CourseReport'
import { CourseRunReport } from '@/pages/CourseRunReport'
import SelfPacedCourse from '@/pages/SelfPacedCourse'
import { UserManagement } from '@/pages/UserManagement'
import { Leaderboard } from '@/pages/Leaderboard'
import CourseCompletionReport from '@/pages/CourseCompletionReport'
import { PresentationView } from '@/pages/PresentationView'
import BlocklyXmlGenerator from '@/pages/BlocklyXmlGenerator'
import { PotatoPiratesSandbox } from '@/pages/PotatoPiratesSandbox'
import { CardGlossary } from '@/pages/CardGlossary'
import { ProfileOnboarding } from '@/pages/ProfileOnboarding'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import { useInitializeApp } from '@/hooks/useInitializeApp'
import { useIsAdmin } from '@/store/authStore'
import { OnboardingProvider, OnboardingModal } from '@/components/onboarding'
import { TourProvider } from '@/components/tour'
import { DEFAULT_ORG_ID } from '@/constants' // kept for courses only

function App() {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const { isInitializing } = useInitializeApp()


  // Show loading screen during app initialization
  if (isLoading || isInitializing) {
    return <LoadingScreen />
  }

  // Public routes (no authentication required)
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/auth" element={<PotatoPiratesAuth />} />
        <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
        <Route path="/auth/verify" element={<VerifyEmail />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<ResetPassword />} />
        <Route path="/login" element={<PotatoPiratesAuth />} />
        <Route path="/join/:runCode" element={<GuestJoin />} />
        <Route path="/join" element={<GuestJoin />} />
        <Route path="/play/:runCode" element={<GuestPlay />} />
        <Route path="/lesson/:lessonId" element={<LessonSession />} />
        <Route path="/cards" element={<CardGlossary />} />
        <Route path="*" element={<GuestAwareRedirect />} />
      </Routes>
    )
  }

  // Protected routes (authentication required)
  // /play/:runCode and /join are rendered WITHOUT the Layout wrapper so guest students
  // see a clean full-screen UI regardless of whether they also happen to be authenticated.

  // If profile onboarding not completed, only allow guest routes + onboarding page
  if (user && !user.profile_onboarding_completed) {
    return (
      <Routes>
        {/* Guest-facing routes remain accessible */}
        <Route path="/play/:runCode" element={<GuestPlay />} />
        <Route path="/join/:runCode" element={<GuestJoin />} />
        <Route path="/join" element={<GuestJoin />} />
        <Route path="/course/:courseCode" element={<SelfPacedCourse />} />
        <Route path="/course/:courseCode/complete" element={<CourseCompletionReport />} />
        <Route path="/present/:runCode" element={<PresentationView />} />

        {/* Profile onboarding page */}
        <Route path="/onboarding/profile" element={<ProfileOnboarding />} />

        {/* Everything else redirects to onboarding */}
        <Route path="*" element={<Navigate to="/onboarding/profile" replace />} />
      </Routes>
    )
  }

  return (
    <OnboardingProvider>
      <Routes>
        {/* Guest-facing routes — no sidebar/header */}
        <Route path="/play/:runCode" element={<GuestPlay />} />
        <Route path="/join/:runCode" element={<GuestJoin />} />
        <Route path="/join" element={<GuestJoin />} />
        <Route path="/course/:courseCode" element={<SelfPacedCourse />} />
        <Route path="/course/:courseCode/complete" element={<CourseCompletionReport />} />
        <Route path="/present/:runCode" element={<PresentationView />} />

        {/* All captain/admin routes — wrapped in Layout */}
        <Route path="*" element={
          <TourProvider>
            <Layout user={user}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/organizations" element={<Organizations />} />
                <Route path="/organizations/:organizationId/courses" element={<CoursesRouteWithParams />} />
                <Route path="/organizations/:organizationId/courses/:courseId" element={<CourseDetail />} />
                <Route path="/organizations/:organizationId/courses/:courseId/report" element={<CourseReport />} />
                <Route path="/organizations/:organizationId/courses/:courseId/runs/:runId/report" element={<CourseRunReport />} />
                <Route path="/courses" element={<Courses organizationId={DEFAULT_ORG_ID} />} />
                <Route path="/my-courses" element={<Courses organizationId={DEFAULT_ORG_ID} myCourses />} />
                <Route path="/lessons" element={<AdminRoute><Lessons /></AdminRoute>} />
                <Route path="/lessons/:lessonId/edit" element={<AdminRoute><LessonBuilder /></AdminRoute>} />
                <Route path="/lessons/:lessonId/preview" element={<LessonBuilder readOnly />} />
                <Route path="/lessons/:lessonId/report" element={<LessonReport />} />
                <Route path="/lesson/:lessonId" element={<LessonSession />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/cards" element={<CardGlossary />} />

                <Route path="/dev/blockly-xml" element={<AdminRoute><BlocklyXmlGenerator /></AdminRoute>} />
                <Route path="/potato-pirates" element={<PotatoPiratesSandbox />} />
                        <Route path="/leaderboard" element={<Leaderboard />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/users/plans" element={<Plans />} />
                <Route path="/add-payment-method" element={<AddPaymentMethod />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/onboarding/profile" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
            <OnboardingModal />
          </TourProvider>
        } />
      </Routes>
    </OnboardingProvider>
  )
}

// Component to extract organizationId from URL params for Courses
function CoursesRouteWithParams() {
  const { organizationId } = useParams<{ organizationId: string }>()

  if (!organizationId) {
    return <Navigate to="/organizations" replace />
  }

  return <Courses organizationId={organizationId} />
}

// Restricts route to admin users only
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin()
  if (!isAdmin) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

// Redirects unauthenticated users back to their active guest session, or to home
function GuestAwareRedirect() {
  const runCode = localStorage.getItem('guest_run_code') || localStorage.getItem('guest_lesson_code')
  const sessionValid = localStorage.getItem('guest_session_valid') === 'true'
  if (runCode && sessionValid) {
    return <Navigate to={`/play/${runCode}`} replace />
  }
  return <Navigate to="/" replace />
}

export default App
